import * as RadixDialog from "@radix-ui/react-dialog";
import { ChevronDown, ChevronUp, Copy, Download, X } from "lucide-react";
import {
  createElement,
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface ContentViewerOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  format: "html" | "text";
  content: string;
  downloadFilename?: string;
}

// Inline styles so the highlight is visible regardless of class-purge timing
// or surrounding CSS resets. Tokens come from the project's tailwind theme
// (see app/assets/tailwind/application.css).
const MARK_STYLE: React.CSSProperties = {
  backgroundColor: "var(--color-highlight-bg)",
  color: "var(--color-highlight)",
  padding: "0 2px",
  borderRadius: "2px",
};
const MARK_CURRENT_STYLE: React.CSSProperties = {
  backgroundColor: "var(--color-highlight)",
  color: "#fff",
  padding: "0 2px",
  borderRadius: "2px",
  outline: "2px solid var(--color-highlight)",
};

interface Segment {
  kind: "t" | "m";
  value: string;
}

function splitTextByQuery(content: string, query: string): Segment[] {
  if (!query) return [{ kind: "t", value: content }];
  const lowerContent = content.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const out: Segment[] = [];
  let cursor = 0;
  let i = lowerContent.indexOf(lowerQuery, cursor);
  while (i !== -1) {
    if (i > cursor) out.push({ kind: "t", value: content.slice(cursor, i) });
    out.push({ kind: "m", value: content.slice(i, i + query.length) });
    cursor = i + query.length;
    i = lowerContent.indexOf(lowerQuery, cursor);
  }
  if (cursor < content.length) out.push({ kind: "t", value: content.slice(cursor) });
  return out;
}

// Map HTML attribute names to React's prop names. Drops on* event handlers and
// any attribute we don't have a safe mapping for (Tiptap output is small in
// surface area — element names + class/href is most of it).
const ATTR_MAP: Record<string, string> = {
  class: "className",
  for: "htmlFor",
  colspan: "colSpan",
  rowspan: "rowSpan",
  tabindex: "tabIndex",
  readonly: "readOnly",
  maxlength: "maxLength",
  autoplay: "autoPlay",
  autocomplete: "autoComplete",
  autocapitalize: "autoCapitalize",
  spellcheck: "spellCheck",
};

function attrsToProps(el: Element): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  for (const attr of Array.from(el.attributes)) {
    if (attr.name.startsWith("on")) continue;
    if (attr.name === "style") continue;
    const propName = ATTR_MAP[attr.name] ?? attr.name;
    props[propName] = attr.value;
  }
  return props;
}

interface RenderResult {
  nodes: ReactNode[];
  matchCount: number;
}

function renderTextWithHighlights(
  text: string,
  query: string,
  currentIndex: number,
  counter: { count: number },
  keyPrefix: string,
): ReactNode {
  if (!query) return text;
  const segs = splitTextByQuery(text, query);
  let textCounter = 0;
  return segs.map((seg) => {
    if (seg.kind === "m") {
      const matchIdx = counter.count++;
      return (
        <mark
          key={`${keyPrefix}-m${matchIdx}`}
          data-search-match="true"
          data-match-index={matchIdx}
          style={matchIdx === currentIndex ? MARK_CURRENT_STYLE : MARK_STYLE}
        >
          {seg.value}
        </mark>
      );
    }
    const tKey = `${keyPrefix}-t${textCounter++}`;
    return <Fragment key={tKey}>{seg.value}</Fragment>;
  });
}

function renderDomNode(
  node: Node,
  query: string,
  currentIndex: number,
  counter: { count: number },
  key: string,
): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.nodeValue ?? "";
    if (!text) return null;
    return (
      <Fragment key={key}>
        {renderTextWithHighlights(text, query, currentIndex, counter, key)}
      </Fragment>
    );
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  const props = attrsToProps(el);
  props.key = key;
  const childNodes = Array.from(el.childNodes);
  if (childNodes.length === 0) {
    return createElement(tag, props);
  }
  const children = childNodes.map((c, idx) =>
    renderDomNode(c, query, currentIndex, counter, `${key}.${idx}`),
  );
  return createElement(tag, props, ...children);
}

function htmlToReact(html: string, query: string, currentIndex: number): RenderResult {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return { nodes: [html], matchCount: 0 };
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="__cvo_root">${html}</div>`, "text/html");
  const root = doc.getElementById("__cvo_root");
  if (!root) return { nodes: [], matchCount: 0 };
  const counter = { count: 0 };
  const nodes = Array.from(root.childNodes).map((node, idx) =>
    renderDomNode(node, query, currentIndex, counter, `n${idx}`),
  );
  return { nodes, matchCount: counter.count };
}

export function ContentViewerOverlay({
  open,
  onOpenChange,
  title,
  format,
  content,
  downloadFilename,
}: ContentViewerOverlayProps) {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [rawQuery, setRawQuery] = useState("");
  const [query, setQuery] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied">("idle");
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(() => setQuery(rawQuery), 150);
    return () => window.clearTimeout(handle);
  }, [rawQuery, open]);

  useEffect(() => {
    if (!open) {
      setRawQuery("");
      setQuery("");
      setCurrentIndex(0);
      setCopyStatus("idle");
    }
  }, [open]);

  // Reset selected match to the first when the query changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: query is the intentional trigger
  useEffect(() => {
    setCurrentIndex(0);
  }, [query]);

  const textSegments = useMemo(
    () => (format === "text" ? splitTextByQuery(content, query) : []),
    [format, content, query],
  );

  const htmlRender = useMemo(
    () => (format === "html" ? htmlToReact(content, query, currentIndex) : null),
    [format, content, query, currentIndex],
  );

  const matchCount =
    format === "html"
      ? (htmlRender?.matchCount ?? 0)
      : textSegments.filter((s) => s.kind === "m").length;

  // After render, scroll the current match into view. The match elements live
  // inside bodyRef — querySelector finds them in document order.
  useLayoutEffect(() => {
    if (!open) return;
    if (matchCount === 0) return;
    const root = bodyRef.current;
    if (!root) return;
    const marks = root.querySelectorAll<HTMLElement>("mark[data-search-match='true']");
    const safeIdx = Math.min(Math.max(currentIndex, 0), marks.length - 1);
    marks[safeIdx]?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [open, currentIndex, matchCount]);

  const handleCopy = useCallback(async () => {
    const text =
      format === "text"
        ? content
        : (bodyRef.current?.innerText ?? bodyRef.current?.textContent ?? "");
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus("copied");
      window.setTimeout(() => setCopyStatus("idle"), 1500);
    } catch {
      setCopyStatus("idle");
    }
  }, [content, format]);

  const handleDownload = useCallback(() => {
    const text =
      format === "text"
        ? content
        : (bodyRef.current?.innerText ?? bodyRef.current?.textContent ?? "");
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadFilename ?? "content.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [content, format, downloadFilename]);

  const stepMatch = useCallback(
    (delta: 1 | -1) => {
      if (matchCount === 0) return;
      setCurrentIndex((idx) => (idx + delta + matchCount) % matchCount);
    },
    [matchCount],
  );

  const counter = query
    ? matchCount === 0
      ? "No matches"
      : `${currentIndex + 1} of ${matchCount}`
    : "";

  const textBody: ReactNode =
    format === "text"
      ? (() => {
          let matchCounter = 0;
          let textCounter = 0;
          return textSegments.map((seg) => {
            if (seg.kind === "m") {
              const matchIdx = matchCounter++;
              return (
                <mark
                  key={`m${matchIdx}`}
                  data-search-match="true"
                  data-match-index={matchIdx}
                  style={matchIdx === currentIndex ? MARK_CURRENT_STYLE : MARK_STYLE}
                >
                  {seg.value}
                </mark>
              );
            }
            return <Fragment key={`t${textCounter++}`}>{seg.value}</Fragment>;
          });
        })()
      : null;

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <RadixDialog.Content
          aria-labelledby={titleId}
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            searchInputRef.current?.focus();
          }}
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-[min(95vw,48rem)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-border bg-card shadow-xl focus:outline-none"
          data-testid="content-viewer-overlay"
        >
          <div className="flex flex-col gap-2 border-b border-border px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <RadixDialog.Title
                id={titleId}
                className="min-w-0 flex-1 truncate text-sm font-semibold text-text-primary"
                title={title}
              >
                {title}
              </RadixDialog.Title>
              <RadixDialog.Close
                aria-label="Close"
                className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
              >
                <X size={16} />
              </RadixDialog.Close>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <input
                  ref={searchInputRef}
                  type="search"
                  value={rawQuery}
                  onChange={(e) => setRawQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    // Enter while the debounce hasn't fired yet commits the
                    // query immediately so the user lands on the first match
                    // without waiting. Subsequent Enters advance.
                    if (rawQuery !== query) {
                      setQuery(rawQuery);
                      return;
                    }
                    stepMatch(e.shiftKey ? -1 : 1);
                  }}
                  placeholder="Search in content…"
                  aria-label="Search in content"
                  data-testid="content-viewer-search"
                  className="min-w-0 flex-1 rounded border border-border bg-surface px-2 py-1 text-xs text-text-primary placeholder:text-text-faint focus:border-accent focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => stepMatch(-1)}
                  disabled={matchCount === 0}
                  aria-label="Previous match"
                  data-testid="content-viewer-prev"
                  className="rounded border border-border p-1 text-text-muted hover:bg-card-hover disabled:opacity-40"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => stepMatch(1)}
                  disabled={matchCount === 0}
                  aria-label="Next match"
                  data-testid="content-viewer-next"
                  className="rounded border border-border p-1 text-text-muted hover:bg-card-hover disabled:opacity-40"
                >
                  <ChevronDown size={14} />
                </button>
                <span
                  className="min-w-[5rem] text-[10px] text-text-faint"
                  data-testid="content-viewer-counter"
                >
                  {counter}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleCopy}
                  aria-label="Copy content"
                  data-testid="content-viewer-copy"
                  className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] text-text-muted hover:bg-card-hover"
                >
                  <Copy size={12} />
                  {copyStatus === "copied" ? "Copied" : "Copy"}
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  aria-label="Download content"
                  data-testid="content-viewer-download"
                  className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] text-text-muted hover:bg-card-hover"
                >
                  <Download size={12} />
                  Download
                </button>
              </div>
            </div>
          </div>
          {format === "html" ? (
            <div
              ref={bodyRef}
              data-testid="content-viewer-body"
              className="prose prose-sm dark:prose-invert max-w-none flex-1 overflow-y-auto px-5 py-4 text-sm text-text-primary"
            >
              {htmlRender?.nodes}
            </div>
          ) : (
            <div
              ref={bodyRef}
              data-testid="content-viewer-body"
              className="flex-1 overflow-y-auto whitespace-pre-wrap break-words px-5 py-4 font-sans text-sm text-text-primary"
            >
              {textBody}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
